import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { FiGitBranch, FiLock, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import RhythmPreview from "../book-builder/RhythmPreview";
import { CAME_FROM, HOW_WORD, KIN_WORD, badge, familyHref, ideaApi } from "./family-data";
import { kinship } from "../../lib/ideas/kinship";
import styles from "./family.module.css";

const named = (member) => member.private ? "a private idea" : member.gone ? "an idea that's no longer here" : member.idea.title;
const by = (idea) => idea?.creator?.handle ? `@${idea.creator.handle}` : idea?.source?.label || "";

// Under an idea's title: where it came from ("Edited from Funk groove 14 · 81% alike").
export function CameFrom({ family, className = "" }) {
  const parent = family?.parents?.find((item) => !item.second);
  if (!parent) return null;
  const second = family.parents.find((item) => item.second);
  return <p className={`${styles.cameFrom} ${className}`}><FiGitBranch aria-hidden="true" />
    <span>{CAME_FROM[parent.how] || "From"} {parent.ref ? <Link href={`/i/${parent.ref}`}>{named(parent)}</Link> : named(parent)}
      {second && <> and {second.ref ? <Link href={`/i/${second.ref}`}>{named(second)}</Link> : named(second)}</>}
      {parent.similarity != null && <> · {parent.similarity}% alike</>}</span></p>;
}

// A member of a family, small: its notation, title, maker, and how it's related.
function Member({ member, compact = false }) {
  if (member.private) return <div className={`${styles.member} ${styles.quiet}`}><FiLock aria-hidden="true" /><span>A private idea</span><small>{HOW_WORD[member.how]}</small></div>;
  if (member.gone) return <div className={`${styles.member} ${styles.quiet}`}><span>No longer available</span><small>{badge(member)}</small></div>;
  return <Link className={styles.member} href={`/i/${member.ref}`}>
    {!compact && member.idea.score && <span className={styles.thumb} aria-hidden="true"><RhythmPreview score={member.idea.score} minWidth={240} fit compact maxHeight="54px" /></span>}
    <span className={styles.memberText}><strong>{member.idea.title}</strong><small>{by(member.idea)}</small></span>
    <span className={styles.badge}>{badge(member)}</span>
  </Link>;
}

// How many notes differ between two versions of a rhythm with the same bars and parts (null when
// their shapes differ, so a count would mean little).
function changedNotes(score, other) {
  if (score?.measures?.length !== other?.measures?.length) return null;
  let changed = 0;
  for (const [index, measure] of score.measures.entries()) {
    const was = other.measures[index];
    if (measure.parts.length !== was.parts.length) return null;
    for (const [partIndex, part] of measure.parts.entries()) part.voices.forEach((voice, voiceIndex) => {
      const before = was.parts[partIndex].voices[voiceIndex]?.notes || [], length = Math.max(voice.notes.length, before.length);
      const key = (item) => item && JSON.stringify([item.notes, item.duration, item.dots || 0, item.ornaments || ""]);
      for (let at = 0; at < length; at++) if (key(voice.notes[at]) !== key(before[at])) changed++;
    });
  }
  return changed;
}

// In the save dialog: the idea this rhythm was made from, how alike it still is, and what saving it
// will say ("edited from Ghost-note pocket"). Nothing shows when that idea can't be seen.
export function BasedOn({ from, score }) {
  const ref = from?.refs?.[0] || null, [state, setState] = useState(null);
  useEffect(() => {
    if (!ref || !score) return undefined;
    let alive = true;
    (async () => {
      const response = await fetch(ideaApi(ref), { credentials: "same-origin", cache: "no-store" });
      const data = await response.json().catch(() => null), idea = response.ok ? data?.idea : null;
      if (!idea) return;
      const { similarity } = await import("../../lib/similarity/similarity");
      let alike = null;
      try { alike = idea.score ? similarity(score, idea.score).score : null; } catch { alike = null; }
      if (alive) setState({ idea, alike, changed: idea.score ? changedNotes(score, idea.score) : null });
    })().catch(() => {});
    return () => { alive = false; };
  }, [ref, score]);
  if (!state) return null;
  const { idea, alike, changed } = state, kin = kinship(alike), how = (CAME_FROM[from.how] || CAME_FROM.edited).toLowerCase();
  return <fieldset className={styles.basedOn}>
    <legend>Based on</legend>
    <div className={styles.basedOnIdea}>
      {idea.score && <span className={styles.thumb} aria-hidden="true"><RhythmPreview score={idea.score} minWidth={240} fit compact maxHeight="64px" /></span>}
      <span><strong>{idea.title}</strong><small>{by(idea) && `by ${by(idea)}`}</small>{alike != null && <span className={styles.badge}>{KIN_WORD[kin] || "Not alike"} · {alike}% alike</span>}</span>
    </div>
    <p>{changed === 0 ? "You haven't changed a note. " : changed ? `You changed ${changed} ${changed === 1 ? "note" : "notes"}. ` : ""}
      {alike == null || kin ? <>Your rhythm will say <strong>{how} {idea.title}</strong>, and it joins that rhythm&apos;s family.</>
        : <>It&apos;s changed too much to join {idea.title}&apos;s family, so it starts a family of its own.</>}</p>
  </fieldset>;
}

// The idea page's strip: where it came from, this idea, what grew from it.
export function FamilyStrip({ ideaRef, title, maker, family }) {
  if (!family) return null;
  const parent = family.parents.find((item) => !item.second), { children, counts } = family;
  return <section className={styles.strip} aria-label="Its family">
    <div className={styles.box}><small>Came from</small>{parent ? <><strong>{parent.ref ? <Link href={`/i/${parent.ref}`}>{named(parent)}</Link> : named(parent)}</strong><span>{badge(parent)}{parent.similarity != null ? " alike" : ""}</span></>
      : <span>Nothing: it started here</span>}</div>
    <FiArrowRight aria-hidden="true" className={styles.arrow} />
    <div className={`${styles.box} ${styles.this}`}><small>This idea</small><strong>{title}</strong>{maker && <span>{maker}</span>}</div>
    <FiArrowRight aria-hidden="true" className={styles.arrow} />
    <div className={styles.box}><small>Grew into</small><strong>{counts.children ? `${counts.children} ${counts.children === 1 ? "idea" : "ideas"}` : "Nothing yet"}</strong>
      {counts.children > 0 && <span>{[counts.drummers ? `by ${counts.drummers} ${counts.drummers === 1 ? "drummer" : "drummers"}` : "", counts.private ? `${counts.private} private` : ""].filter(Boolean).join(", ")}</span>}</div>
    {(parent || counts.children > 0) && <Link className={styles.treeLink} href={familyHref(ideaRef)}><FiGitBranch aria-hidden="true" /> Open the family tree</Link>}
  </section>;
}

// The Family panel (beside a rhythm, and on its idea page): where it came from, what grew from it.
export function FamilyPanel({ ideaRef, family }) {
  if (!family) return <p className={styles.note} role="status">Finding its family…</p>;
  const { parents, children, counts } = family;
  return <div className={styles.panel}>
    <h3>Came from</h3>
    {parents.length ? <div className={styles.members}>{parents.map((member, index) => <Member key={member.ref || index} member={member} />)}</div>
      : <p className={styles.note}>It started here.</p>}
    <h3>Grew into {counts.children > 0 && <span>{counts.children} {counts.children === 1 ? "idea" : "ideas"}{counts.drummers ? ` · ${counts.drummers} ${counts.drummers === 1 ? "drummer" : "drummers"}` : ""}</span>}</h3>
    {children.length ? <div className={styles.members}>{children.map((member, index) => <Member key={member.ref || index} member={member} />)}</div>
      : !counts.private && <p className={styles.note}>Nothing yet. Grow, vary or edit it, and what you save joins its family.</p>}
    {counts.private > 0 && <p className={`${styles.member} ${styles.quiet}`}><FiLock aria-hidden="true" /><span>{counts.private} more grew from it privately</span></p>}
    {(parents.length > 0 || counts.children > 0) && <Link className={styles.treeLink} href={familyHref(ideaRef)}><FiGitBranch aria-hidden="true" /> Open the family tree</Link>}
  </div>;
}

// The family tree page: generations as rows, each idea joined to the one it came from by how it
// changed and how alike it stayed. Private ideas are boxes with no address; second parents are
// dashed. On a narrow screen the tree scrolls inside its frame.
const W = 196, H = 118, GAP = 28, ROW = 84;
export function FamilyTree({ ideaRef }) {
  const [state, setState] = useState({ loading: true, error: "", idea: null, tree: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: "", idea: null, tree: null });
    fetch(ideaApi(ideaRef, "?family=tree"), { credentials: "same-origin", cache: "no-store" }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (alive) setState(response.ok && data.tree ? { loading: false, error: "", idea: data.idea, tree: data.tree } : { loading: false, error: data.error || "This idea's family isn't available.", idea: null, tree: null });
    }).catch(() => { if (alive) setState({ loading: false, error: "Ideas are temporarily unavailable.", idea: null, tree: null }); });
    return () => { alive = false; };
  }, [ideaRef]);
  const layout = useMemo(() => state.tree && arrange(state.tree), [state.tree]), frame = useRef(null);
  // The idea itself in view, where the tree is wider than the screen.
  useEffect(() => {
    const root = layout?.placed.find((node) => node.generation === 0);
    if (frame.current && root) frame.current.scrollLeft = root.x + W / 2 - frame.current.clientWidth / 2;
  }, [layout]);
  if (state.loading) return <p className={styles.note} role="status">Finding its family…</p>;
  if (state.error) return <div className={styles.missing}><h1>{state.error}</h1><Link href="/community">Find rhythms in Explore</Link></div>;
  const { idea, tree } = state;
  return <section className={styles.treePage} aria-labelledby="family-title">
    <Link className={styles.back} href={`/i/${ideaRef}`}><FiArrowLeft aria-hidden="true" /> {idea.title}</Link>
    <div className={styles.treeHead}>
      <div><h1 id="family-title">Family of {idea.title}</h1><p>{summary(tree)}</p></div>
      <p className={styles.legend}><span className={styles.solid} aria-hidden="true" /> came from <span className={styles.dashed} aria-hidden="true" /> second parent</p>
    </div>
    <div className={styles.treeFrame} ref={frame}>
      <div className={styles.treeArea} style={{ width: layout.width, height: layout.height }}>
        <svg width={layout.width} height={layout.height} className={styles.lines} aria-hidden="true">
          {layout.lines.map((line) => <g key={line.key}>
            <path d={line.path} className={line.second ? styles.secondLine : styles.line} />
            <text x={line.labelX} y={line.labelY} className={styles.lineLabel}>{line.label}</text>
          </g>)}
        </svg>
        {layout.placed.map((node) => <div key={node.key} className={`${styles.node} ${node.generation === 0 ? styles.root : ""} ${node.private || node.gone ? styles.quietNode : ""}`}
          style={{ left: node.x, top: node.y, width: W, height: H }}>
          {node.private ? <><FiLock aria-hidden="true" /><strong>{node.count} private {node.count === 1 ? "idea" : "ideas"}</strong><small>Only their makers see them</small></>
            : node.gone ? <><strong>No longer available</strong><small>It kept its place in the family</small></>
            : <Link href={`/i/${node.ref}`} aria-label={`${node.idea.title}${node.generation === 0 ? ", this idea" : ""}`}>
              {node.idea.score ? <span className={styles.thumb} aria-hidden="true"><RhythmPreview score={node.idea.score} minWidth={240} fit compact maxHeight="50px" /></span>
                : <span className={styles.thumbEmpty} aria-hidden="true">{node.idea.recording ? "Recording" : node.idea.kind}</span>}
              <strong>{node.idea.title}</strong><small>{[by(node.idea), node.generation === 0 ? "this idea" : ""].filter(Boolean).join(" · ")}</small>
            </Link>}
        </div>)}
      </div>
    </div>
  </section>;
}

// "Edited from Funk groove 14. 8 ideas grew from it, 2 of them private."
function summary(tree) {
  const parents = tree.nodes.filter((node) => node.generation === -1), grown = tree.nodes.filter((node) => node.generation > 0);
  const hidden = grown.filter((node) => node.private).reduce((sum, node) => sum + node.count, 0), count = grown.filter((node) => !node.private).length + hidden;
  const how = tree.edges.find((edge) => edge.child === tree.ref && !edge.second)?.how;
  return [parents.length ? `${CAME_FROM[how] || "It came from"} ${parents.map((node) => node.private ? "a private idea" : node.gone ? "an idea that's no longer here" : node.idea.title).join(" and ")}.` : "",
    count ? `${count} ${count === 1 ? "idea" : "ideas"} grew from it${hidden ? `, ${hidden} of them private` : ""}.` : "Nothing has grown from it yet."].filter(Boolean).join(" ");
}

// Where each idea of a tree goes: a row for each generation, each idea under (or over) the one it's
// joined to, and a line for each link, labelled "edited · 81% alike".
function arrange(tree) {
  const keyOf = (node) => node.ref || node.key, rows = new Map(), left = new Map(), STEP = W + GAP;
  for (const node of tree.nodes) { if (!rows.has(node.generation)) rows.set(node.generation, []); rows.get(node.generation).push(node); }
  const generations = [...rows.keys()].sort((a, b) => a - b);
  // The idea first, then each row outward from it: every idea centred with its siblings on the one
  // it's joined to (private boxes last), pushed apart where they'd overlap.
  for (const generation of [0, ...generations.filter((g) => g > 0), ...generations.filter((g) => g < 0).sort((a, b) => b - a)]) {
    const anchorOf = (node) => {
      const edge = tree.edges.find((item) => generation > 0 ? item.child === keyOf(node) && left.has(item.parent) : item.parent === keyOf(node) && left.has(item.child));
      return edge ? left.get(generation > 0 ? edge.parent : edge.child) : 0;
    };
    const groups = new Map();
    for (const node of rows.get(generation)) { const anchor = generation ? anchorOf(node) : 0; groups.set(anchor, [...(groups.get(anchor) || []), node]); }
    const wanted = [...groups].flatMap(([anchor, nodes]) => nodes.sort((a, b) => Boolean(a.private) - Boolean(b.private))
      .map((node, index) => [node, anchor + (index - (nodes.length - 1) / 2) * STEP])).sort((a, b) => a[1] - b[1]);
    let last = -Infinity;
    const placed = wanted.map(([node, want]) => { last = Math.max(want, last + STEP); return [node, last, want]; });
    const drift = placed.reduce((sum, [, at, want]) => sum + at - want, 0) / placed.length;
    for (const [node, at] of placed) left.set(keyOf(node), at - drift);
  }
  const lowest = Math.min(...left.values()), width = Math.max(...left.values()) - lowest + W + 2;
  const positions = new Map(), placed = [];
  generations.forEach((generation, rowIndex) => rows.get(generation).forEach((node) => {
    const x = left.get(keyOf(node)) - lowest + 1, y = rowIndex * (H + ROW);
    positions.set(keyOf(node), { x, y });
    placed.push({ ...node, key: keyOf(node), x, y });
  }));
  const lines = tree.edges.filter((edge) => positions.has(edge.parent) && positions.has(edge.child)).map((edge) => {
    const from = positions.get(edge.parent), to = positions.get(edge.child);
    const x1 = from.x + W / 2, y1 = from.y + H, x2 = to.x + W / 2, y2 = to.y, middle = y1 + (y2 - y1) / 2;
    const label = edge.private ? "private" : [HOW_WORD[edge.how]?.toLowerCase() || edge.how, edge.similarity != null ? `${edge.similarity}% alike` : ""].filter(Boolean).join(" · ");
    return { key: `${edge.parent}>${edge.child}`, second: edge.second, label, path: `M${x1},${y1} V${middle} H${x2} V${y2}`, labelX: x2 + 8, labelY: middle + (y2 - middle) / 2 + 4 };
  });
  return { width, height: generations.length * H + (generations.length - 1) * ROW + 2, placed, lines };
}
